import { defaultTo, groupBy, sumBy } from "lodash-es";
import debounce from "lodash-es/debounce";
import first from "lodash-es/first";
import isArray from "lodash-es/isArray";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import availabilityStore from "../stores/availabilityStore";
import cartStore from "../stores/cart/cartStore";
import categoryStore from "../stores/categories/categoryStore";
import deliveryDatesStore from "../stores/deliveryDates/deliveryDatesStore";
import deliveryFeeStore from "../stores/deliveryFeeStore";
import posthogStore from "../stores/posthogStore";
import productStore from "../stores/product/productStore";
import promotionStore from "../stores/promotionStore";
import uiStore from "../stores/uiStore";
import theme from "../themes/theme";
import { sendButtonClickEvent, sendLoginEvent } from "./eventTelemetry";
import { logger } from "./logger";
import { buildListInfo } from "./trackingUtils";
import type { OrderType } from "./types/productOrderTypes";
import type { CartItem, LostSaleItem, Product } from "./types/productTypes";
import {
   type GAAddtionalData,
   type GAClaim,
   type GAItem,
   type GAItemProduct,
   type GAListInformation,
   LIST_TYPE,
   type PosthogOrderCompleted,
   type PosthogProduct,
   type TrackingInfo
} from "./types/trackingTypes";
import { isExternalLink, translateOrderType } from "./utils";
import { getCartIdFromStorage } from "./utils/storage/storageUtils";

const productToGA4 = (product?: Product, appliedPromotionIds?: string[]): GAItemProduct => {
   if (isUndefined(product)) {
      return {};
   }
   const categories = categoryStore.findCategoryKeysBySku(product.sku);
   const category = categories.length > 0 ? first(categories) : { cat1: null, cat2: null, cat3: null };

   // Only purchase passes the campaigns that apply; other events report every campaign the product is linked to
   const voucherifyPromotionIds: string[] =
      appliedPromotionIds ??
      promotionStore.availablePromotions.data.flatMap((promotion) =>
         promotion.products.includes(product.sku) ? [promotion.id] : []
      );

   const priceBeforeDiscount = product.priceBeforeDiscount ?? 0;
   const price = product.price ?? 0;
   const M3Discount = priceBeforeDiscount - price;
   const M3DiscountPercentage = M3Discount ?? ((M3Discount / priceBeforeDiscount) * 100).toFixed(2);

   const labels = product.labels.map((label) => label.text);

   /*
   Why does this need special handling?
   if (product.expired && includes(SEASONAL_PRODUCTS, product.sku)) {
      labels.push("Utgått");
   }
   */

   const promotionTypes: string[] = [];
   if (voucherifyPromotionIds.length) {
      promotionTypes.push("Voucherify");
   }
   if (M3Discount > 0) {
      promotionTypes.push("M3");
   }

   return {
      item_id: product.sku,
      item_name: product.name,
      item_brand: product.brand,
      item_category: category?.cat1,
      item_category2: category?.cat2,
      item_category3: category?.cat3,
      price: product.price,
      unit_size: product.unit,
      callout: labels,
      promotionTypes: promotionTypes,
      voucherifyIds: voucherifyPromotionIds,
      m3_discount: `${M3DiscountPercentage.toString()}%`
   };
};

const productSkusToGAItem = (items: { sku: string; qty: number; position?: number }[], listInformation?: GAListInformation) => {
   const GAItems: GAItem[] = items.reduce((arr, item) => {
      const product = productStore.resolveSku(item.sku);
      if (!product) {
         logger.warn("tracking", "Unable to find product when sending add to cart event", item);
         return arr;
      }

      const GAItem = {
         ...productToGA4(product),
         ...buildListInfo(listInformation, item.position),
         quantity: item.qty
      } satisfies GAItem;

      arr.push(GAItem);
      return arr;
   }, [] as GAItem[]);
   return GAItems;
};

const debouncedSendSearch = (searchType: string, searchResults: number, searchTerm: string) => {
   sendEvent("ga4_search", {
      search_type: searchType,
      search_results: searchResults,
      search_term: searchTerm
   });
};

export const GA_NAVIGATION_MAIN_CATEGORY = "hovedkategori";

export const GA_NAVIGATION_SUB_CATEGORY = "underkategori";

// Debounce the search event so that we dont get separate events for each new term (l, le, lett, lettm, lettme, etc)
export const sendSearch = debounce(debouncedSendSearch, 1000);

export const sendFilter = (filterType: string, filterContent: string) => {
   sendEvent("ga4_filter", {
      filter_type: filterType,
      filter_content: filterContent
   });
};

export const sendLoginStart = () => {
   sendEvent("ga4_login_start", { method: uiStore.loginMode });
};

export const sendLoginStep = () => {
   sendEvent("ga4_login_step", { method: uiStore.loginMode });
};

export const sendLoginSuccess = () => {
   sendEvent("ga4_login", { method: uiStore.loginMode });
   sendLoginEvent();
};

export const sendLoginFailed = () => {
   sendEvent("ga4_login_failed");
};

// Sent if we load a cart that was originally created during edit order mode, while not in edit mode.
export const sendCartCleared = () => {
   sendEvent("ga4_cart_cleared");
};

const itemsToPosthogProducts = (items: { sku: string; qty: number }[]): PosthogProduct[] => {
   return items
      .map((item) => {
         const { sku, qty } = item;
         const product = productStore.resolveSku(sku);
         if (product) {
            const price = product.price || 0;
            const url = `${location.origin}${productStore.getUrl(product)}`;
            const optional = product?.manuallabels?.length ? { labels: product.manuallabels } : {};

            return {
               sku,
               product_id: sku,
               quantity: qty,
               price,
               name: product.name,
               url,
               ...optional
            };
         }
         return null;
      })
      .filter((p) => p) as PosthogProduct[];
};
export const sendAddToCart = (items: { sku: string; qty: number; position?: number }[], listInformation: GAListInformation) => {
   const GAItems: GAItem[] = productSkusToGAItem(items, listInformation);
   const sumOfProductPrices = sumBy(GAItems, (item) => defaultTo(item.quantity, 1) * defaultTo(item.price, 0));

   sendEvent("ga4_add_to_cart", {
      ecommerce: {
         item_list_id: listInformation.type,
         item_list_name: listInformation.name,
         item_list_length: listInformation.length,
         currency: "NOK",
         quantity: sumBy(items, "qty"),
         value: sumOfProductPrices,
         items: GAItems
      }
   });

   const posthogProducts = itemsToPosthogProducts(items);
   const cartId = getCartIdFromStorage() || "";
   for (const posthogProduct of posthogProducts) {
      posthogStore.captureEvent("Product Added", {
         cart_id: cartId,
         list_type: listInformation.type,
         list_name: listInformation.name,
         list_length: listInformation.length,
         ...posthogProduct
      });
   }
};

export const sendRemoveFromCart = (
   items: { sku: string; qty: number; position?: number }[],
   listInformation: GAListInformation
) => {
   const GAItems: GAItem[] = productSkusToGAItem(items, listInformation);
   const sumOfProductPrices = sumBy(GAItems, (item) => defaultTo(item.quantity, 1) * defaultTo(item.price, 0));

   sendEvent("ga4_remove_from_cart", {
      ecommerce: {
         item_list_id: listInformation.type,
         item_list_name: listInformation.name,
         item_list_length: listInformation.length,
         currency: "NOK",
         quantity: sumBy(items, "qty"),
         value: sumOfProductPrices,
         items: GAItems
      }
   });
   const posthogProducts = itemsToPosthogProducts(items);
   const cartId = getCartIdFromStorage() || "";
   for (const posthogProduct of posthogProducts) {
      posthogStore.captureEvent("Product Removed", { cart_id: cartId, ...posthogProduct });
   }
};

export const sendProductClick = (product: Product, listInformation: GAListInformation, position?: number) => {
   sendEvent("ga4_select_item", {
      ecommerce: {
         item_list_id: listInformation.type,
         item_list_name: listInformation.name,
         item_list_length: listInformation.length,
         items: [
            {
               ...productToGA4(product),
               ...buildListInfo(listInformation, position)
            }
         ]
      }
   });

   const posthogProduct = itemsToPosthogProducts([{ sku: product.sku, qty: 1 }])[0];
   posthogStore.captureEvent("Product Clicked", {
      ...posthogProduct,
      list_type: listInformation.type,
      list_name: listInformation.name,
      list_length: listInformation.length
   });
};

export const sendAddItemsToFavoriteList = (products: Product[], listInformation: GAListInformation, position?: number) => {
   const items = products.map((product) => {
      return {
         ...productToGA4(product),
         ...buildListInfo(listInformation, position)
      };
   });
   const value = items.reduce((acc, item) => acc + (item.price || 0), 0);
   sendEvent("ga4_add_to_favorite_list", {
      ecommerce: {
         item_list_id: listInformation.type,
         item_list_name: listInformation.name,
         value,
         items
      }
   });
};

export const sendRemoveItemFromFavoriteList = (sku: string, listInformation: GAListInformation, position?: number) => {
   const product = productStore.resolveSku(sku);
   if (!isNil(product)) {
      sendEvent("ga4_remove_from_favorite_list", {
         ecommerce: {
            item_list_id: listInformation.type,
            item_list_name: listInformation.name,
            items: [
               {
                  ...productToGA4(product),
                  ...buildListInfo(listInformation, position)
               }
            ]
         }
      });
   }
};

export const sendAddCartToFavoriteList = (cartName: string) => {
   const cartItems = cartStore.items;
   const skus = cartItems.map(({ sku }) => sku);
   const products = productStore.resolveSkus(skus);
   sendAddItemsToFavoriteList(products, { type: LIST_TYPE.CART, name: cartName, length: cartItems.length });
};

export const sendMiniCartVisible = () => {
   sendViewCart("ga4_view_mini_cart");
};

export const sendCartVisible = () => {
   sendViewCart("ga4_view_cart");
};

export const sendModal = (name: string) => {
   sendEvent("ga4_modal_view", { modal_name: name });
};

export const sendPageNotFound = (url: string) => {
   sendEvent("ga4_page_not_found", {
      link_url: `${theme.baseUrl}${url}`
   });
};

/**
 * Sends the cart contents to Google Analytics, with a specific event name.
 */
const sendViewCart = (eventName: "ga4_view_mini_cart" | "ga4_view_cart") => {
   const cartTotal = cartStore.getCartTotal();
   const itemSkusAndQty = cartStore.items.map((item) => ({
      sku: item.sku,
      qty: item.qty
   }));
   const items = productSkusToGAItem(itemSkusAndQty);

   const cartInfo = {
      order_type: translateOrderType(cartStore.orderType, theme.storeCompanyName),
      order_interval: deliveryDatesStore.getCurrentDelivery()?.interval,
      order_class: cartStore.isEditing() ? "change" : "new"
   };

   sendEvent(eventName, {
      ...(eventName === "ga4_view_cart" && cartInfo),
      ecommerce: {
         currency: "NOK",
         value: cartTotal.total,
         items
      }
   });
};

export const sendViewUnavailableItems = () => {
   const items = availabilityStore.unavailable || [];

   const itemPayload: GAItem[] = items.map((item) => {
      const product = productStore.resolveSku(item.productNumber);
      return {
         ...productToGA4(product)
      };
   });

   sendEvent("ga4_view_unavailable_items", {
      ecommerce: {
         items: itemPayload
      }
   });
};

export const sendProductDetails = (product: Product, stock_status?: string) => {
   sendEvent("ga4_view_item", {
      ecommerce: {
         currency: "NOK",
         value: product.price,
         items: [
            {
               ...productToGA4(product),
               stock_status
            }
         ]
      }
   });
};

export const sendViewTeaser = (teaserName?: string, teaserType?: string, trackingListName?: string) =>
   sendEvent("ga4_view_promotion", {
      creative_slot: trackingListName,
      creative_name: teaserName,
      creative_type: teaserType
   });

export const sendSelectTeaser = (teaserName?: string, teaserType?: string, trackingListName?: string, url?: string) => {
   const urlToReport = isExternalLink(url) ? url : `${theme.baseUrl}${url}`;

   sendEvent("ga4_select_promotion", {
      creative_slot: trackingListName,
      creative_name: teaserName,
      creative_type: teaserType,
      link_url: urlToReport
   });
};

export const sendInternalLinkClick = (url?: string, trackingLocationName?: string, trackingName?: string) =>
   sendEvent("ga4_internal_link_click", {
      link_slot: trackingLocationName,
      link_url: `${theme.baseUrl}${url}`,
      link_name: trackingName
   });

export const sendButtonClick = (trackingName?: string) => {
   sendEvent("ga4_button_click", {
      button_name: trackingName
   });
   sendButtonClickEvent(trackingName);
};

export const sendChipClick = (trackingObject: { name: string; context: string }) => {
   sendEvent("ga4_filter", {
      filter_content: trackingObject.name,
      filter_type: trackingObject.context
   });
};

export const sendTelephoneClick = () => sendEvent("ga4_telephone_click", {});

export const sendSignup = (trackingFormName?: string) =>
   sendEvent("ga4_signup", {
      signup_name: trackingFormName
   });

export const sendNewClaim = (claim: GAClaim) => {
   sendEvent("ga4_new_claim", {
      claim
   });
};

export const getLostSalesTotal = (lostSales: LostSaleItem[]) =>
   sumBy(lostSales, (lostSale) => lostSale.quantity * (productStore.resolveSku(lostSale.sku)?.price ?? 0));

export const sendPurchase = (
   ids: string | string[],
   cartItems: CartItem[],
   wasEditing: boolean,
   orderTotal: number,
   orderType: OrderType,
   lostSalesTotal = 0,
   appliedPromotionIdsBySku: Record<string, string[]> = {}
) => {
   const id = isArray(ids) ? ids[0] : ids;

   const products = cartItems.map((cartItem) => {
      const productFromCartItem = productStore.resolveSku(cartItem.sku);
      const appliedPromotionIds = wasEditing ? [] : (appliedPromotionIdsBySku[cartItem.sku] ?? []);
      return {
         ...productToGA4(productFromCartItem, appliedPromotionIds),
         quantity: cartItem.qty
      };
   });

   const posthogPayload: PosthogOrderCompleted = {
      order_id: id,
      affiliation: theme.storeCompanyName,
      total: orderTotal,
      shipping: deliveryFeeStore.currentDeliveryCharge().currentFee?.fee || 0,
      currency: "NOK",
      products: cartItems.map((cartItem) => {
         const product = productStore.resolveSku(cartItem.sku);
         const url = product ? `${location.origin}${productStore.getUrl(product)}` : "";
         const optional = product?.manuallabels?.length ? { labels: product.manuallabels } : {};
         return {
            sku: cartItem.sku,
            product_id: cartItem.sku,
            quantity: cartItem.qty,
            price: product?.price || 0,
            total: (product?.price || 0) * cartItem.qty,
            name: product?.name || "",
            url,
            ...optional
         };
      })
   };

   posthogStore.captureEvent("Order Completed", posthogPayload);

   for (const product of posthogPayload.products) {
      posthogStore.captureEvent("Product Purchased", {
         ...product,
         order_id: posthogPayload.order_id
      });
   }

   sendEvent("ga4_purchase", {
      order_type: translateOrderType(orderType, theme.storeCompanyName),
      order_interval: deliveryDatesStore.getCurrentDelivery()?.interval,
      order_class: wasEditing ? "changed order" : "new order",
      ecommerce: {
         currency: "NOK",
         coupon: null,
         transaction_id: id,
         value: orderTotal,
         deliveryFee: deliveryFeeStore.currentDeliveryCharge().currentFee?.fee,
         items: products
      }
   });

   sendEvent("ga4_lost_sales_ratio", {
      value: orderTotal === 0 ? 0 : lostSalesTotal / orderTotal
   });
};

export const sendOrderTypeChange = () => {
   sendEvent("ga4_change_order_type", {
      order_type: translateOrderType(cartStore.orderType, theme.storeCompanyName)
   });
};

export const sendDeliveryDateChange = () => {
   sendEvent("ga4_change_delivery_date", {
      action: "updated"
   });
};

export const sendOrderIntervalChange = () => {
   sendEvent("ga4_change_order_interval", {
      action: "updated",
      order_interval: deliveryDatesStore.getCurrentDelivery()?.interval
   });
};

// Keep track of queued impressions
const impressions: { product: Product; listInformation: GAListInformation; position?: number }[] = [];
let impressionTimeout: number | null = null;

export const queueImpression = (product: Product, listInformation: GAListInformation, position?: number) => {
   impressions.push({ product, listInformation, position });

   if (impressions.length > 25) {
      sendQueuedImpressions();
   }

   if (impressionTimeout === null) {
      impressionTimeout = window.setTimeout(sendQueuedImpressions, 2_000);
   }
};

const sendQueuedImpressions = () => {
   if (impressions.length > 0) {
      const itemPayload: GAItem[] = impressions.map(({ product, listInformation, position }) => ({
         ...productToGA4(product),
         ...buildListInfo(listInformation, position)
      }));

      const impressionsGrouped = groupBy(itemPayload, (item) => JSON.stringify([item.item_list_id, item.item_list_name]));
      impressions.length = 0;

      for (const items of Object.values(impressionsGrouped)) {
         const item = items[0];
         sendEvent("ga4_view_item_list", {
            ecommerce: {
               item_list_id: item.item_list_id,
               item_list_name: item.item_list_name,
               num_items: items.length,
               items: items
            }
         });
      }
   }

   if (impressionTimeout !== null) {
      clearTimeout(impressionTimeout);
      impressionTimeout = null;
   }
};

export const sendErrorMessage = (error: TrackingInfo) => {
   sendEvent("ga4_error_message", {
      error_context: error.context,
      error_text: error.text
   });
};

export const sendAccordionClick = (accordionTitle: string, accordionCategory?: string) => {
   sendEvent("ga4_accordion_information", {
      accordion_title: accordionTitle,
      accordion_category: accordionCategory
   });
};

export const sendViewItemList = (id: LIST_TYPE, name: string, products: Product[]) => {
   const listInformation: GAListInformation = { type: id, name };
   const items: GAItem[] = products
      .filter((product): product is Product => !isNil(product))
      .map((product) => ({
         ...productToGA4(product),
         ...buildListInfo(listInformation)
      }));
   if (items.length) {
      sendEvent("ga4_view_item_list", {
         ecommerce: {
            ...buildListInfo(listInformation),
            num_items: items.length,
            items
         }
      });
   }
};

const sendEvent = (event: string, additionalData?: GAAddtionalData) => {
   const eventPayload = {
      event,
      ...additionalData,
      _clear: true
   };

   logger.log("tracking", "Event submitted: ", eventPayload);
   window.dataLayer.push(eventPayload);
};
